/** Module de calcul de masse molaire 
 * Ce module utilise le tableau périodique au format JSON en assets
 * cf https://github.com/Bowserinator/Periodic-Table-JSON 
*/
import periodicTable from '../assets/PeriodicTableJSON'
import { adjust, inPercentage } from './util'

/**
 * 
 * @param {*} formula la formule chimique à traduire (CaSO3, CO2, CA(OH)2, ...)
 * retourne un objet {atomic_mass, elements: [{symbol,atomic_mass,count}]}
 */
export const molarMass = (formula) => {
    try {
        const { elements, atomic_mass } = computeMolarMass(formula)
        elements.forEach(el => {
            el.massPercentage = massPercentage(el, atomic_mass)
        }) // On calcul le pourcentage de masse de chaque élément par rapport à la masse totale de la formule
        return { elements, atomic_mass }
    } catch (e) {
        throw e
    }
}

/** Module nécessaire pour extraire les différents éléments d'une formule chimique
 * retourne un tableau [{symbol,atomic_mass,count}]
 */
export const getElements = (formula) => {
    const result = []
    const elements = formula.match(/[A-Z][a-z]?[0-9]*/g) // on obtient l'ensemble des elements et leur chiffre s'il y en a un
    elements.forEach(e => {
        const value = e.match(/[A-Z][a-z]?/g) // On obtien le symbole
        let number = e.match(/([0-9]+)/g) // On obtien le symbole
        const element = periodicTable.elements.find(el => el.symbol === value[0]) // On récupère la valeur dans le tableau
        if (element != null) {
            let count = 1
            if (number) {
                count = parseInt(number[0])
            }
            result.push({ symbol: element.symbol, atomic_mass: adjust(element.atomic_mass, 5), count })
        } else {
            throw new Error() // Erreur émise
        }
    })
    return result
}

/** Fonction permettant de décomposer une formule et de récupérer la description complète de chaque élément 
 * Cette fonction est utilisée uniquement pour l'affichage
*/
export const findElements = (formula) => {
    const elements = formula.match(/[A-Z][a-z]?/g)
    const result = []
    if (elements) {
        elements.forEach(e => {
            const element = periodicTable.elements.find(el => el.symbol === e) // On récupère la valeur dans le tableau
            if (element) {
                if (!containsElement(element, result)) {
                    result.push({ ...element, atomic_mass: adjust(element.atomic_mass, 5) })
                }
            }
        })
    }
    return result
}

/** Fonction permettant de récupérer l'ensemble des éléments tout en gérant les groupements
 * Types de groupements : ()X ou .X
 */
const handleGroupment = (formula) => {
    let elements = []
    const parenthesis = formula.match(/\([A-Za-z1-9]*\)[1-9]/g) // récupération d'une parenthèse entière (contenu + nbr)
    if (parenthesis) {
        parenthesis.forEach(p => {
            const parenthesisElements = parenthisGroupment(p)
            elements = [...elements, ...parenthesisElements]
            formula = formula.replace(p, '') // On enlève le groupement de la formule complète
        })
    }
    const dot = formula.match(/[.].*/g) // Récupération du contenu du point
    if (dot) {
        dot.forEach(d => {
            const dotElements = dotGroupment(d)
            elements = [...elements, ...dotElements]
            formula = formula.replace(d, '')
        })
    }
    if (formula.length > 0) { // Si la formule n'est pas entièremeent consumée
        elements = [...elements, ...getElements(formula)]
    }
    return elements
}

/** Gestion d'un groupement type "paranthèse" */
const parenthisGroupment = (grp) => {
    const nbr = parseInt(grp.substring(grp.indexOf(')') + 1)) // le nombre pour multiplier l'ensemble de la formule
    const subFormula = grp.substring(grp.indexOf('(') + 1, grp.indexOf(')')) // La sous-formule à analyser
    const elements = getElements(subFormula) // on récupère les éléments du groupement
    elements.forEach(el => el.count *= nbr)
    return elements
}

/** Gestion d'un groupement type "point" */
const dotGroupment = (grp) => {
    const value = grp.match(/[.][0-9]*[,|/]?[0-9]*/g)[0].substring(1) // Le nombre pour multiplier l'ensemble de la formule
    let nbr = 1
    if (value.includes('/')) {
        nbr = parseInt(value.substring(0, value.indexOf('/'))) / parseInt(value.substring(value.indexOf('/') + 1))
    } else if (value.includes(',')) {
        nbr = parseFloat(value.replace(',', '.'))
    } else if(value) {
        nbr = parseInt(value)
    }
    const subFormula = grp.replace(`.${value}`, '')
    const elements = getElements(subFormula)
    elements.forEach(el => el.count *= nbr)
    return elements
}

/** Fonction effectuant le calcul de la masse molaire sur base des éléments analysés et de la formule fournie
 */
const computeMolarMass = (formula) => {
    const elements = handleGroupment(formula)
    let atomic_mass = 0
    const elementSet = set(elements) // On regroupe les doublons
    elementSet.forEach(e1 => {
        atomic_mass += e1.atomic_mass * e1.count
    })
    return { elements: elementSet, atomic_mass: adjust(atomic_mass, 3) }
}

/** Permet de regrouper les éléments en enlevant les doublons */
const set = (elements) => {
    const set = []
    elements.forEach(el => {
        if (!containsElement(el, set)) { // On vérifie que le set contient l'élément
            set.push({ ...el, count: 0 }) // Sinon on l'ajoute dans le set  (on reset la masse atomique, elle sera calculée après)
        }
    })
    elements.forEach(el => {
        set.forEach(s => {
            if (el.symbol === s.symbol) {
                s.count += el.count
            }
        })
    })
    return set
}

/** Should return true if the array contains the element */
const containsElement = (element, array) => {
    let contains = false
    array.forEach(a => {
        if (element.symbol === a.symbol)
            contains = true
    })
    return contains
}

/** Should return the mass percentage of an element */
const massPercentage = (element, total) => {
    return adjust(inPercentage(element.atomic_mass * element.count / total), 2)
}